<template lang="pug">
  card.registration-form
    h1.big-section-heading.main-heading {{ $t('registration.heading') }}
    p {{ $t('registration.subHeading') }}
    .sections
      transition(name='fade')
        section.section(
          v-for='(step, stepIndex) in steps'
          :key='stepIndex'
          v-if='currentStep === stepIndex'
        )
          label(v-for='(field, fieldId) in step.fields', :key='fieldId')
            h4.label-heading {{ $t(field.label) }}
            input.square-input(
              v-if='field.type !== "select"'
              v-model='field.value'
              v-validate.disable='field.validation'
              :type='field.type'
              :placeholder='$t(field.label)'
              :name='fieldId'
            )
            label.arrow-drop(v-if='field.type === "select"')
              select.square-input(
                v-model='field.value'
                v-validate.disable='field.validation'
                :name='fieldId'
              )
                option(v-for='option in field.options', :key='option.value', :value='option.value') {{ $t(option.label) }}
            span.validation {{ errors.first(fieldId) }}
    button.button.button--gray-outline(
      v-if='currentStep > 0'
      @click='lastStep'
    ) {{ $t('registration.prevStep') }}
    button.button.button--blue(
      v-if='currentStep < steps.length - 1'
      @click='validateAndNextStep'
    ) {{ $t('registration.nextStep') }}
    button.button.button--blue(
      v-if='currentStep >= steps.length - 1'
      @click='register'
    ) {{ $t('registration.button') }}
</template>

<script>
import _ from 'lodash'
import card from '@/components/common/card.vue'

export default {
  components: { card },
  data: () => ({
    currentStep: 0,
    steps: [
      {
        skipable: false,
        fields: {
          firstname: {
            label: 'registration.fields.firstname',
            type: 'text',
            value: '',
            validation: 'required|max:255'
          },
          lastname: {
            label: 'registration.fields.lastname',
            type: 'text',
            value: '',
            validation: 'required|max:255'
          },
          title: {
            label: 'registration.fields.title',
            type: 'select',
            value: '',
            options: [
              { label: 'registration.titleOption.mr', value: 'mr' },
              { label: 'registration.titleOption.mrs', value: 'mrs' },
              { label: 'registration.titleOption.dr', value: 'dr' }
            ]
          }
        }
      },
      {
        skipable: true,
        fields: {
          company: { label: 'registration.fields.company', type: 'text', value: '', validation: 'required|max:255' },
          phone: { label: 'registration.fields.phone', type: 'text', value: '', validation: 'required|max:255' },
          fax: { label: 'registration.fields.fax', type: 'text', value: '', validation: 'max:255' }
        }
      },
      {
        skipable: true,
        fields: {
          address: { label: 'registration.fields.address', type: 'text', value: '', validation: 'required|max:255' },
          city: { label: 'registration.fields.city', type: 'text', value: '', validation: 'required|max:255' },
          zipCode: { label: 'registration.fields.postalCode', type: 'text', value: '', validation: 'required|max:255' },
          country: { label: 'registration.fields.country', type: 'text', value: '', validation: 'max:255' },
          province: { label: 'registration.fields.province', type: 'text', value: '', validation: 'max:255' }
        }
      }
    ]
  }),
  methods: {
    clearForm () {
      _.map(this.steps, (step, stepIndex) => {
        _.map(step.fields, (field, fieldIndex) => {
          this.steps[stepIndex].fields[fieldIndex].value = ''
        })
      })
    },
    async validateAndNextStep () {
      const result = await this.$validator.validate()
      if (result) this.currentStep++
    },
    lastStep () {
      this.currentStep--
    },
    async register () {
      this.$ga.event('Button', 'Clicked', `Register Account ${this.$i18n.locale.toUpperCase()}`)
      try {
        this.$store.dispatch('startTask', { id: 'register' })
        await this.$store.dispatch('quote/completeProfile',
          _.reduce(this.steps, (result, value, key) => {
            _.map(this.steps[key].fields, (field, fieldId) => {
              result[fieldId] = field.value
            })
            return result
          }, {})
        )
        this.$store.dispatch('endTask', { id: 'register' })
        this.$ga.event('Registration', 'Success', 'Registration Successful')
        this.$notify({ group: 'general', type: 'success', title: 'Success', text: this.$t('registration.successMessage'), duration: -1 })
        this.clearForm()
        this.$router.push('/')
      } catch (error) {
        console.error(error)
        this.$store.dispatch('clearTasks')
        this.$ga.event('Registration', 'Error', error.message)
        if (error.code === 'invalid_password') { this.currentStep = 0 }
        this.$notify({ group: 'general', type: error.type || 'error', title: error.title, text: error.message, duration: -1 })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/scss/abstract/variables.scss';

  // Sections transitions
  .sections { position: relative; }
  .section { left: 0; right: 0; }
  .fade-enter-active, .fade-leave-active { transition: opacity 450ms; }
  .fade-enter { opacity: 0; }
  .fade-enter-to { opacity: 1; }
  .fade-leave { opacity: 1; }
  .fade-leave-to { opacity: 0; }
  .fade-leave-active { position: absolute; }

  // validation
  .validation {
    display: block;
    min-height: 1rem;
    padding-top: .3em;

    text-align: left;
    font-size: .8em;
    color: $red;
  }

  .registration-form {
    padding: 3em;
    width: 100%;
    max-width: 450px;
    text-align:center;
    margin: 0 auto;
  }

  .card {
    background: transparent;
  }

  .main-heading {
    margin: 0 0 0.3em 0;
    color: $blue;
    font-weight: 400;
    text-transform: none;

    @media only screen and (max-width: $mobile) {
      margin: 0 auto;
      font-size: 3rem;
    }
  }

  p {
    margin-bottom: 2em;
    line-height: 1.5em;
    font-size: 1em;
    margin-top:2em;
    display: block;
  }

  .label-heading {
    margin: 0 0 .2em;
    font-size: .85em;
    font-weight: 400;
    color: $blue;
    text-align: left;
    margin-bottom: 1em;
  }

  .login {
    display: block;
    margin: 2em auto 0 auto;

    a {
      display: inline-block;
      margin: 0 .5em;
      color: $blue;
      font-weight: 600;
    }
  }

  label {
    display: block;
    margin-bottom: .25em;

    + label {
      margin-top: .75em;
    }
  }
  input {
    width: 100%;
  }

  button {
    margin: 2em 1em 0;
  }
</style>
